import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    useTheme,
    Alert,
    Button,
    Stack,
    useMediaQuery,
    TablePagination,
    Card,
    CardHeader,
    IconButton,
    Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getCheckInHistory } from '../services/user';
import { useAuth } from '../hooks/useAuth';
import { DateTime } from 'luxon';
import { format } from 'date-fns';

const CheckInHistory = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [loading, setLoading] = useState(true);
    const [checkIns, setCheckIns] = useState([]);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const lastFetchTime = useRef(null);
    const FETCH_COOLDOWN = 30000; // 30 seconds
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    
    // Date filter states
    const [startDate, setStartDate] = useState(() => {
        return DateTime.now().minus({ months: 1 }).startOf('day');
    });
    const [endDate, setEndDate] = useState(() => {
        return DateTime.now().endOf('day');
    });

    const fetchCheckIns = useCallback(async (force = false) => {
        if (!user?.ClientID) return;

        // Check if we're within the cooldown period and not forcing refresh
        if (!force && lastFetchTime.current && Date.now() - lastFetchTime.current < FETCH_COOLDOWN) {
            return;
        }
        
        try {
            setLoading(true);
            setError(null);
            const formattedStartDate = startDate.toFormat('yyyy-MM-dd');
            const formattedEndDate = endDate.toFormat('yyyy-MM-dd');
            
            const response = await getCheckInHistory(user.ClientID, formattedStartDate, formattedEndDate);
            
            if (response.success && Array.isArray(response.data)) {
                setCheckIns(response.data);
            } else {
                setError('No check-in history available');
            }
            lastFetchTime.current = Date.now();
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to load check-in history');
        } finally {
            setLoading(false);
        }
    }, [user?.ClientID, startDate, endDate]);

    useEffect(() => {
        fetchCheckIns();
        // Set up auto-refresh if viewing recent check-ins
        if (startDate.hasSame(DateTime.now(), 'day') && endDate.hasSame(DateTime.now(), 'day')) {
            const interval = setInterval(() => fetchCheckIns(true), 5 * 60 * 1000); // Refresh every 5 minutes
            return () => clearInterval(interval);
        }
    }, [startDate, endDate, fetchCheckIns]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const formatTime = (dateString) => {
        return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    };

    // Get current page of check-ins
    const displayedCheckIns = checkIns.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    if (!user?.ClientID) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="error">Unable to load user information</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Check-in History
            </Typography>

            <Card sx={{ mb: 4 }}>
                <CardHeader
                    title="Check-in Records"
                    action={
                        <Tooltip title="Refresh check-ins">
                            <IconButton onClick={() => fetchCheckIns(true)} disabled={loading}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    }
                />

                {/* Date Filters */}
                <LocalizationProvider dateAdapter={AdapterLuxon}>
                    <Stack 
                        direction={isMobile ? "column" : "row"} 
                        spacing={2} 
                        sx={{ mb: 4, mt: 2, px: 2 }}
                        alignItems={isMobile ? "stretch" : "center"}
                    >
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={setStartDate}
                            maxDate={endDate}
                            sx={{ width: isMobile ? '100%' : 220 }}
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={setEndDate}
                            minDate={startDate}
                            maxDate={DateTime.now()}
                            sx={{ width: isMobile ? '100%' : 220 }}
                        />
                        <Button
                            variant="contained"
                            onClick={() => fetchCheckIns(true)}
                            startIcon={<SearchIcon />}
                            sx={{ height: isMobile ? '40px' : '56px' }}
                        >
                            Search
                        </Button>
                    </Stack>
                </LocalizationProvider>

                {error ? (
                    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{error}</Alert>
                ) : loading && checkIns.length === 0 ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                                        <TableCell sx={{ color: 'white' }}>Date & Time</TableCell>
                                        <TableCell sx={{ color: 'white' }}>Location</TableCell>
                                        <TableCell sx={{ color: 'white' }}>Station</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedCheckIns.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                No check-ins found for the selected date range
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayedCheckIns.map((checkIn, index) => (
                                            <TableRow 
                                                key={index}
                                                sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}
                                            >
                                                <TableCell>{formatTime(checkIn.checkInTime)}</TableCell>
                                                <TableCell>{checkIn.locationId}</TableCell>
                                                <TableCell>{checkIn.stationName}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={checkIns.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                    </>
                )}
            </Card>
        </Box>
    );
};

export default CheckInHistory; 